import React, { useMemo } from "react";
import styles from "./DressingChangeFrequency.module.scss";
import { getCounts, getSummary, getNextText, getDayTicks, checkSchedule, buildCalendarStrip } from "./utils/scheduleUtils";
import { CardHeader } from "./components/CardHeader";
import { SummaryBox } from "./components/SummaryBox";
import { QuickPicksPanel } from "./components/QuickPicksPanel";
import { ModeSelector } from "./components/ModeSelector";
import { EveryPanel } from "./components/EveryPanel";
import { TimesPanel } from "./components/TimesPanel";
import { WeekdaysPanel } from "./components/WeekdaysPanel";
import { PrnPanel } from "./components/PrnPanel";
import { CalendarStrip } from "./components/CalendarStrip";
import { TimelineBar } from "./components/TimelineBar";
import { ValidationBanner } from "./components/ValidationBanner";
import type { ProductItem, Schedule, SchedulePatch, DayOfWeek } from "./types";

const getStartDate = (): Date => {
  const d = new Date();
  d.setHours(8, 0, 0, 0);
  return d;
};

interface DressingChangeFrequencyCardProps {
  item: ProductItem;
  type: "primary" | "secondary";
  index: number;
  editable: boolean;
  schedule: Schedule;
  maxDays: number;
  onChange: (patch: SchedulePatch) => void;
  onToggleDay: (day: DayOfWeek) => void;
}

export const DressingChangeFrequencyCard: React.FC<DressingChangeFrequencyCardProps> = ({
  item,
  editable,
  schedule,
  maxDays,
  onChange,
  onToggleDay,
}) => {
  const startDate = useMemo(getStartDate, []);

  const counts = useMemo(() => getCounts(schedule, startDate), [schedule, startDate]);
  const summaryText = useMemo(() => getSummary(schedule), [schedule]);
  const nextText = useMemo(() => getNextText(schedule, counts, startDate), [schedule, counts, startDate]);
  const ticks = useMemo(() => getDayTicks(schedule, startDate), [schedule, startDate]);
  const validation = useMemo(() => checkSchedule(schedule, maxDays), [schedule, maxDays]);
  const strip = useMemo(() => buildCalendarStrip(schedule, counts, startDate, styles as Record<string, string>), [schedule, counts, startDate]);

  if (!editable) {
    return (
      <article className={styles.card}>
        <CardHeader item={item} />
        <div className={styles.view_summary}>
          <span className={styles.summary_label}>Reads as</span>
          <span className={styles.summary_text}>{summaryText}</span>
          {nextText && <span className={styles.summary_next}>{nextText}</span>}
        </div>
      </article>
    );
  }

  return (
    <article className={styles.card}>
      <CardHeader item={item} />

      {/* Tier 1 — full-width quick picks */}
      <div className={styles.quick_picks_section}>
        <QuickPicksPanel schedule={schedule} startDate={startDate} onChange={onChange} />
      </div>

      {/* Tier 2 — full-width "Reads as" summary, reacts to both presets and manual config */}
      <SummaryBox summaryText={summaryText} nextText={nextText} />

      {/* Tier 3 — 40/60 split: configuration (left) | visual preview (right) */}
      <div className={styles.config_preview_row}>
        <div className={styles.config_panel}>
          <ModeSelector mode={schedule.mode} onChange={onChange} />

          {schedule.mode === "every" && <EveryPanel schedule={schedule} onChange={onChange} />}
          {schedule.mode === "times" && <TimesPanel schedule={schedule} onChange={onChange} />}
          {schedule.mode === "weekdays" && <WeekdaysPanel schedule={schedule} onToggleDay={onToggleDay} />}
          {schedule.mode === "prn" && <PrnPanel schedule={schedule} onChange={onChange} />}
        </div>

        <div className={styles.preview_panel}>
          <CalendarStrip strip={strip} schedule={schedule} />
          <TimelineBar ticks={ticks} schedule={schedule} />
        </div>
      </div>

      {/* Tier 4 — full-width validation banner (form-level concern, not just calendar) */}
      <ValidationBanner validation={validation} onChange={onChange} />
    </article>
  );
};
